import { setCompleteVehicleSelected } from "action/garage/setCompleteVehicleSelected";
import { closeModal } from "action/modal/closeModal";
import { openModal } from "action/modal/openModal";
import { getVehicleSelection } from "api/garage/getVehicleSelection";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { IVehicle } from "interfaces/session/IVehicle";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import qs from "query-string";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { trackFitmentEvent } from "util/analytics/trackFitmentEvent";
import { TRACKING_EVENT_NAME } from "util/analytics/tracking.service";
import yieldAnalyticsEvent from "util/analytics/yieldAnalyticsEvent";
import { PRODUCT_PAGE_TYPES } from "util/product/product-constants";
import { refreshData } from "util/refreshData";
import getCustomSizeQuery from "util/url/getCustomSizeQuery";
import { selectVehicle } from "../../action/garage/selectVehicle";
import { equalVehicles } from "../../util/vehicle/equal-vehicles";
import { validateVehicle } from "../../util/vehicle/validate-vehicle";
import { YEAR_MAKE_MODEL, YEAR_MAKE_MODEL_ENGINE } from "./constants";

function VehicleSelectorModal(props: any) {
  const { t } = useTranslation("common");
  const {
    title = t("Add a New Vehicle"), defaultMake, defaultModel, defaultYear,
    category = {}, // forceNextAttribute = false,
    fitmentOptionsPrompt: defaultFitmentOptionsPrompt = [],
    fitmentOptionsDetails: defaultFitmentOptionsDetails = {}, noModal = false,
    forceTrigger, requireFullVehicleSelection, addNewVehicle,
  } = props;
  const dispatch = useDispatch();
  const router = useRouter();
  const { query } = router;
  const { pathname } = query;
  const customSizes = getCustomSizeQuery();
  const [isMobile] = useScreenSize();

  const { selectedVehicle, vehicleSelectionOptions } = useSelector((state: RootState) => state.garage);
  const { businessCustomer } = useSelector((state: RootState) => state.authenticationState);
  const { useVehicleSelectionOptionsModal, urlInformation, currentPageType } = useSelector((state: RootState) => state.utils);

  const {
    tier0 = "", tier1: t1, tier2: t2, tier3: t3,
  } = urlInformation;

  const tier1 = t1 || query.tier1;
  const tier2 = t2 || query.tier2;
  const tier3 = t3 || query.tier3;

  function postVehicleApi(data) {
    const { nextVehicleSelectionOption } = data || {};
    dispatch(setCompleteVehicleSelected(Boolean(!nextVehicleSelectionOption)));
    if (nextVehicleSelectionOption === null || !data) {
      dispatch(closeModal());
    } else {
      dispatch(openModal("vehicleSelectionOptionsModal", {
        forceTrigger,
        defaultVehicleData: { ...data, focusedVehicleSelectionOption: data.nextVehicleSelectionOption },
        requireFullVehicleSelection,
      }));
    }
  }
  useEffect(() => {
    if (forceTrigger) {
      // In case of forceTrigger, only base vehicle will be used
      dispatch(openModal("vehicleSelectionOptionsModal", {
        forceTrigger, requireFullVehicleSelection, fitmentOptionsPrompt, addNewVehicle,
      }));
    } else {
      // API Call with all selected fitment options
      const make = query.make || urlInformation.make || selectedVehicle?.make;
      const model = query.model || urlInformation.model || selectedVehicle?.model;
      const year = query.year || selectedVehicle?.year;
      if ((!customSizes || (customSizes && Object.keys(customSizes).length === 0)) && pathname
        && validateVehicle(selectedVehicle) && currentPageType && !PRODUCT_PAGE_TYPES.includes(currentPageType)) {
        try {
          refreshData({
            pathname,
            isMobile: true,
            selectedVehicle,
          }).then(({ keysToUpdate }) => {
            postVehicleApi(keysToUpdate.vehicleSelectionOptions);
          });
        } catch (error) {
          // console.error("Error fetching data:", error);
        }
      } else {
        try {
          getVehicleSelection({
            url: `/${pathname}`,
            make,
            model,
            year,
            fitmentOptions: selectedVehicle?.fitmentOptions || [],
            t0: tier0,
            t1: tier1,
            t2: tier2,
            t3: tier3,
          }).then(({ data }) => {
            postVehicleApi(data);
          });
        } catch (error) {
          // console.error("Error fetching data:", error);
        }
      }

      // If next option is null, don't open modal
      // else open using everything in garage vehicle
    }
  }, [useVehicleSelectionOptionsModal, selectedVehicle, forceTrigger, JSON.stringify(customSizes)]);

  const selectionOrder = businessCustomer ? YEAR_MAKE_MODEL_ENGINE : (props.selectionOrder || YEAR_MAKE_MODEL);

  const [attributeOptions, setAttributeOptions] = useState([]);
  const [nextAttribute, setNextAttribute] = useState();
  const [draftVehcile, setDraftVehcile] = useState<IVehicle>({
    make: defaultMake,
    model: defaultModel,
    year: defaultYear,
    fitmentOptions: selectedVehicle ? selectedVehicle.fitmentOptions : [],
  });
  const [isVisible, setIsVisible] = useState(false);

  // To avoid storing in redux we are fetching the latest fitment details after calling get-page-details
  const [fitmentDetails, setFitmentDeails] = useState({
    fitmentOptionsDetails: defaultFitmentOptionsDetails,
    fitmentOptionsPrompt: defaultFitmentOptionsPrompt,
  });

  const { fitmentOptionsDetails, fitmentOptionsPrompt } = fitmentDetails;
  const attributeTitle = fitmentOptionsDetails[fitmentOptionsPrompt[0]]?.title;

  const parsedQP = qs.parse(location.search);

  const categoryforVehicleDetails = {
    tier0: category?.tier0 || urlInformation?.tier0,
    tier1: category?.tier1 || urlInformation?.tier1 || parsedQP.tier1,
    tier2: category?.tier2 || urlInformation?.tier2 || parsedQP.tier2,
    tier3: category?.tier3 || urlInformation?.tier3 || parsedQP.tier3,
  };

  const vehicle = {
    make: props.defaultMake,
    model: props.defaultModel,
    year: props.defaultYear,
  };

  // logic to hide popup when getVehicleDetails is in progess during when make, model, year is present,
  // else there was pop shown, for very small duration.
  useEffect(() => {
    if (!validateVehicle(vehicle)) {
      setIsVisible(true);
      return;
    }
    if (!categoryforVehicleDetails.tier0) {
      props.onHide();
      return;
    }

    if (fitmentOptionsPrompt.length > 0) {
      // todo: set fitment title
      /*
       const fitmentTitle = selectedFitmentOptions
            .reduce((acc, fo) => (`${acc} ${fo.optionValue}`), "");
          dispatch(setFitmentTitle(fitmentTitle));
      */
      const attribute = fitmentOptionsPrompt[0];
      const fitmentDetail = fitmentOptionsDetails[attribute];
      // todo: check this
      // dispatch(setVehicleDetails(response));
      if (fitmentDetail && fitmentDetail.fitmentOptionValues.length === 0) {
        props.onHide();
      } else {
        setNextAttribute(attribute);
        setAttributeOptions(fitmentDetail?.fitmentOptionValues || []);
        setIsVisible(true);
        yieldAnalyticsEvent(() => {
          trackFitmentEvent(TRACKING_EVENT_NAME.FITMENT_PROMPTED, {
            fitmentOptionName: attribute,
            fitmentOptionTitle: fitmentDetail?.title,
            unselectedFitments: fitmentOptionsPrompt,
            selectedFitments: draftVehcile.fitmentOptions.map((fo) => fo.optionName),
            ...categoryforVehicleDetails,
          });
        });
      }
    } else {
      props.onHide();
    }
  }, [props.defaultMake && props.defaultModel && props.defaultYear]);

  function onSelectAttribute(_vehicle: any) {
    dispatch(selectVehicle(_vehicle));
    props.onHide();
  }

  function handleOnHide() {
    yieldAnalyticsEvent(() => trackFitmentEvent(TRACKING_EVENT_NAME.FITMENT_MODAL_CLOSED, {
      vehicle: draftVehcile,
      ...categoryforVehicleDetails,
    }));

    if (validateVehicle(draftVehcile) && !equalVehicles(draftVehcile, selectedVehicle, true)) {
      dispatch(selectVehicle(draftVehcile));
    }
    props.onHide();
  }

  if (!isVisible) {
    return null;
  }

  const isValidDraftVehicle = validateVehicle(draftVehcile);

  // function renderModalContainer() {
  //   return (
  //     <div
  //       className={styles.modalContent}
  //       data-testid="vehicle-selector-modal"
  //       data-clarity-unmask="true"
  //     >

  //       <div className="hidden-xs hidden-sm hidden-md">

  //         <HideIf show={isValidDraftVehicle}>

  //           <div className="d-flex align-items-center">

  //             <Image
  //               unoptimized
  //               width={60}
  //               height={60}
  //               alt={draftVehcile.make}
  //               className={styles.icon_src}
  //               src={createMakeImageSource(draftVehcile)}
  //             />

  //             <Text size={6} type={TEXT_TYPE.white} className="p-5">
  //               {t(`Select ${attributeTitle || ""} for`)}
  //               <b className="p-r-10">{` ${getVehicleName(draftVehcile, ["year", "make", "model"])}`}</b>
  //             </Text>
  //           </div>
  //         </HideIf>

  //         <HideIf show={!isValidDraftVehicle}>

  //           <div className="d-flex align-items-center p-r-10" style={{ height: "100%" }}>

  //             <i
  //               className="fa fa-car p-r-10"
  //               style={{
  //                 color: "#fff",
  //                 fontSize: "1.5em",
  //               }}
  //             />

  //             <Text className={styles.label} size={6} type={TEXT_TYPE.white}>{title}</Text>
  //           </div>
  //         </HideIf>
  //       </div>

  //       <FlexVehicleSelector
  //         autoScroll={false}
  //         onSelectVehicle={props.onAddVehicle}
  //         onSelectAttribute={onSelectAttribute}
  //         category={categoryforVehicleDetails}
  //         attributeOptions={attributeOptions}
  //         draftVehcile={(_: any) => setDraftVehcile(_)}
  //         {...{
  //           defaultMake, defaultModel, defaultYear, selectionOrder,
  //         }}
  //         autoFocus
  //         focusEl={getFocusEl(selectionOrder, {
  //           defaultMake, defaultModel, defaultYear, attributeOptions, nextAttribute,
  //         })}
  //         clientSideRefresh={props.clientSideRefresh}
  //         setData={props.setData}
  //         attributeTitle={attributeTitle}
  //         setFitmentDeails={setFitmentDeails}
  //       />

  //       <div className="hidden-xs hidden-sm hidden-md">

  //         <button onClick={handleOnHide} type="button" className={`close ${styles.crossIcon}`} aria-label="close">

  //           <span aria-hidden="true">×</span>
  //         </button>
  //       </div>
  //     </div>
  //   );
  // }

  return null;

  // if (noModal) {
  //   return (
  //     <div className={styles.vehicle_container}>
  //       {renderModalContainer()}
  //     </div>
  //   );
  // }

  // return (
  //   <PAModal
  //     dialogClassName={styles.vehicleSelectorModel}
  //     modelBodyClassName={styles.modelBody}
  //     headerClassName={`${styles.headerClassName} hidden-lg`}
  //     backdropClassName={styles.backdropClassName}
  //     // showHeader={isMobile}
  //     {...{
  //       size: MODAL_SIZE.LARGE,
  //       onHide: handleOnHide,
  //       enforceFocus: false,
  //     }}
  //     header={{
  //       title: !isValidDraftVehicle ? title : `${t(`Select ${attributeTitle || ""} for`)} ${getVehicleName(draftVehcile, ["year", "make", "model"])}`,
  //       iconSrc: !isValidDraftVehicle ? null : createMakeImageSource(draftVehcile),
  //       iconSrcClass: styles.icon_src,
  //       iconClass: !isValidDraftVehicle ? `fa-car ${styles.header_label}` : null,
  //     }}
  //   >

  //     {renderModalContainer()}
  //   </PAModal>
  // );
}

VehicleSelectorModal.propTypes = {
  onHide: PropTypes.func.isRequired,
  onAddVehicle: PropTypes.func.isRequired,
};

export default VehicleSelectorModal;
